import { media } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import {
  AppTile,
  PAGINATED_APP_GRID_CONFIG,
  PaginatedAppGrid,
  RegulatedSectorAppTilePreferredHosting,
  usePaginatedAppGrid,
} from '@atlassian/mpac-app-tile';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import {
  BackToTopButton,
  BodyNormal,
  Filters,
  FluidContainer,
  H4,
  MatchingLabelSkeleton,
  Meta,
  NoResultView,
  NotFound,
  PartialOutageError,
  SortDropdown,
  sortOptionsConfig,
} from '@atlassian/mpac-primitives';
import { useSearchFilters } from '@atlassian/mpac-search';
import { GetCollection } from '@atlassian/mpac-services-client';
import Telemetry from '@atlassian/mpac-telemetry';
import { ApolloErrorHandler, usePageRouteSourceProvider, useParams } from '@atlassian/mpac-ui';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import { css, styled } from '@compiled/react';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { CollectionPageTask } from '../../../telemetry/tasks';
import { Hero } from '../../common/Hero/Hero';
import { HeroSkeleton } from '../../common/Hero/Hero.styles';
import { FEDRAMP_PAGE_URL_SLUG } from './constants';
import { filterDefinitions } from './filters/collectionFiltersConfig';
import { useCollectionPageAnalytics, useCollectionTelemetry } from './hooks';
import { UseCaseCard } from './UseCaseCard';
import { UseCaseCardWithImage } from './UseCaseCard/UseCaseCardWithImage';

import type { TileData } from '@atlassian/mpac-app-tile';
import type {
  CollectionFragment,
  GetCollectionQuery,
  GetCollectionQueryVariables,
} from '@atlassian/mpac-services-client';

const Container = styled.div({
  paddingBottom: token('space.800'),
});

const HeroWrapper = styled.div<{ useCompactSpacing: boolean }>({
  marginBottom: (props) => (props.useCompactSpacing ? token('space.0') : token('space.500')),
});

export const ErrorContainer = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  padding: `${token('space.400')}`,
});

const filterWrapperStyle = css({
  marginTop: token('space.200'),
  marginBottom: token('space.400'),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [media.below.md]: {
    marginBottom: token('space.300'),
  },

  [media.below.sm]: {
    display: 'block',
    marginTop: token('space.150'),
  },
});

const CenteredH4 = styled(H4)({
  textAlign: 'center',
  marginTop: token('space.400'),
  marginBottom: token('space.200'),
  [media.below.sm]: {
    textAlign: 'left',
  },
});

const SortAndCountWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: token('space.500'),
  marginBottom: token('space.200'),

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: token('space.200'),
  },
});

const H2 = ({ children, ...props }) => (
  <BodyNormal as="h2" {...props}>
    {children}
  </BodyNormal>
);

const BodyStyled = styled(H2)({
  fontWeight: 400,
});

const AGENT_STORE_COLLECTION_SLUGS = ['artificial-intelligence', 'atlassian-rovo', 'ai-and-rovo'];

// eslint-disable-next-line max-lines-per-function, complexity
const CollectionPage = () => {
  const source = useSource();
  const environment = useEnvironment();
  const { slug } = useParams();
  const { pageRouteSource, setPageRouteSource } = usePageRouteSourceProvider();
  const [capturedRouteSource, setCapturedRouteSource] = useState<string | null>(null);
  const {
    // states
    allFilterDefinitions,
    filterOptions,
    selectedOptions,
    filterStatus,
    sortOptions,
    commitSortKey,
    commitOptions,
    selectedMobileSortOption,
    // booleans
    showAllFilters,

    // set states
    setShowAllFilters,

    // functionality
    onClearFilter,
    onClearAllFilters,
    onSelectedOptionsChange,
    onApplyFilters,
    onSelectedSortOptionChange,
    onCloseMobileDialog,
    onCommitSortOptionSelect,
    handleClearFilters,
  } = useSearchFilters(filterDefinitions);

  const { enableMpacAgentsPageView } = useStatsig();

  // Show filters only for specific collection slugs
  const shouldShowFilters = enableMpacAgentsPageView();

  // When query variables like 'sort' changes, initial query
  // is re-fetched where collection data is reset by apollo graphql
  // Persisting collection data in local state to avoid showing loader
  // for collection data in such cases which would ideally be picked
  // from cache even though refetched
  const [collection, setCollection] = useState<CollectionFragment | null>(null);

  const {
    totalCount,
    rawData,
    appList,
    isLoading,
    isLoadingMore,
    showLoadMoreButton,
    fetchMoreApps,
    initialFetchError,
    fetchMoreError,
  } = usePaginatedAppGrid<GetCollectionQuery, GetCollectionQueryVariables>({
    query: GetCollection,
    queryOptions: {
      variables: {
        slug: slug ?? '',
        filters: shouldShowFilters ? commitOptions : {},
        sort: commitSortKey,
        after: 0,
        first: PAGINATED_APP_GRID_CONFIG.initialFetchCount,
        skipCollection: !!collection,
      },
    },
    parseResponse: (data) => {
      return {
        appList:
          data?.marketplaceCollection?.apps?.edges?.map((edge) => edge.node as TileData) ?? [],
        hasNextPage: Boolean(data?.marketplaceCollection?.apps?.pageInfo.hasNextPage),
        totalCount: data?.marketplaceCollection?.apps?.pageInfo.totalCount || 0,
      };
    },
  });

  useEffect(() => {
    CollectionPageTask.start();
  }, []);

  useEffect(() => {
    setCollection(null);
  }, [slug]);

  useEffect(() => {
    if (pageRouteSource) {
      setCapturedRouteSource(pageRouteSource);
      setPageRouteSource(null);
    }
  }, [pageRouteSource, setPageRouteSource]);

  useEffect(() => {
    if (rawData?.marketplaceCollection?.collection) {
      setCollection(rawData.marketplaceCollection.collection);
    }
  }, [rawData?.marketplaceCollection?.collection]);

  // Track perf for overall page loads & transitions (slug change)
  useLayoutEffect(() => {
    if (totalCount > 0) {
      Telemetry.timeToFirstMeaningfulPaint(performance.now());
    }
  }, [slug, totalCount]);

  useCollectionTelemetry({
    slug,
    initialFetchError,
    collection,
    isLoadingMore,
    fetchMoreError,
    appList: rawData?.marketplaceCollection?.apps,
    sortKey: commitSortKey,
  });

  const {
    fireOnTileClickEvent,
    fireOnLoadMoreClickEvent,
    fireOnSortOptionSelectEvent,
    fireOnSortDropdownClickEvent,
  } = useCollectionPageAnalytics({
    slug,
    initialFetchError,
    collection,
    fetchMoreError,
    appsCount: appList?.length || 0,
    sortKey: commitSortKey || sortOptionsConfig?.[0].value,
    isLoading,
    isLoadingMore,
  });

  const handleOnTileClick = (app: TileData, index: number) => {
    const attributes = {
      collectionType: collection?.slug,
      hitRank: index + 1,
    };
    fireOnTileClickEvent(attributes, app);
  };

  const handleOnLoadMoreClick = async () => {
    const attributes = {
      collectionType: collection?.slug,
      pageNumber: Math.ceil(appList.length / PAGINATED_APP_GRID_CONFIG.fetchMoreCount),
    };
    fireOnLoadMoreClickEvent(attributes);

    fetchMoreApps({
      first: PAGINATED_APP_GRID_CONFIG.fetchMoreCount,
      variables: { skipCollection: true },
    });
  };

  const handleOnSortOptionSelect = (sortOption) => {
    const attributes = {
      collectionType: collection?.slug,
      filterType: 'sort',
      filterValue: sortOption.value,
    };
    fireOnSortOptionSelectEvent(attributes);

    onCommitSortOptionSelect(sortOption);
  };

  const handleOnClearFiltersClick = () => {
    handleClearFilters();
  };

  const handleOnSortDropdownClick = () => {
    const attributes = {
      collectionType: collection?.slug,
    };
    fireOnSortDropdownClickEvent(attributes);
  };

  const showNoResultView =
    shouldShowFilters && !isLoading && totalCount === 0 && !initialFetchError;
  // If slug exist but  there is initialfetcherror and networkerror is 401
  if (slug && initialFetchError) {
    //When slug exists but initialfetcherror is not found that is 404
    if (initialFetchError?.message === 'NOT_FOUND') {
      return <NotFound />;
    }
    return (
      <ApolloErrorHandler
        error={initialFetchError}
        context={{ page: 'CollectionPage', operationName: 'GetCollection' }}
        renderError={() => {
          CollectionPageTask.failed(initialFetchError);
          return (
            <ErrorContainer data-testid="error">
              <PartialOutageError level="global" />
            </ErrorContainer>
          );
        }}
        nonServerErrorCallback={() => CollectionPageTask.abort()}
      />
    );
  }
  //If slug does not exist  or there is initialfetcherror but category is not found or showpageerror is true
  if (!slug || initialFetchError) {
    return (
      <ErrorContainer>
        <PartialOutageError level="global" />
      </ErrorContainer>
    );
  }

  const primaryBreadcrumb =
    enableMpacAgentsPageView() && capturedRouteSource === 'agentsPage'
      ? {
          title: 'Marketplace AI hub',
          href: '/ai-hub',
        }
      : {
          title: 'Marketplace',
          href: '/',
        };
  const isAIRovoCollection =
    enableMpacAgentsPageView() && collection
      ? AGENT_STORE_COLLECTION_SLUGS.includes(collection.slug)
      : false;

  const useCaseHasImages = collection?.useCases?.values?.every((useCase) => useCase.image) ?? false;

  // Remove 'apps' suffix from collection.name if present to avoid duplication in meta title
  const trimmedCollectionName = collection?.name?.trim() ?? '';
  const collectionNameForMeta = trimmedCollectionName.replace(/\s*apps$/i, '');
  const metaTitle = `${collectionNameForMeta} apps for Atlassian products`;

  return (
    <Container>
      {collection && <Meta title={metaTitle} description={collection.heroSection.description} />}
      <HeroWrapper useCompactSpacing={isAIRovoCollection}>
        {collection ? (
          <Hero
            backgroundColor={collection.heroSection.backgroundColor}
            primaryBreadcrumbTitle={primaryBreadcrumb.title}
            primaryBreadcrumbHref={primaryBreadcrumb.href}
            secondaryBreadcrumbTitle={collection.name}
            title={collection.heroSection.title}
            description={collection.heroSection.description}
            imageUrl={collection.heroSection.image.url}
            imageAltText={collection.heroSection.image.altText}
            isBreadcrumbCompact={!isAIRovoCollection}
            hasBackground={!isAIRovoCollection}
            heroContentGridStartDesktop={isAIRovoCollection ? 1 : 2}
            heroContentGridEndDesktop={isAIRovoCollection ? 8 : 9}
            isImageBelowDescription={isAIRovoCollection}
            useCompactSpacing={isAIRovoCollection}
          />
        ) : (
          <HeroSkeleton />
        )}
      </HeroWrapper>

      {collection?.useCases ? (
        useCaseHasImages ? (
          <UseCaseCardWithImage
            heading={collection.useCases.heading}
            useCases={collection.useCases.values}
          />
        ) : (
          <UseCaseCard
            heading={collection.useCases.heading}
            useCases={collection.useCases.values}
          />
        )
      ) : null}

      <FluidContainer>
        {shouldShowFilters && (
          <>
            <CenteredH4>Browse apps</CenteredH4>
            <div css={filterWrapperStyle}>
              <Filters
                filterOptions={filterOptions}
                filterDefinitions={allFilterDefinitions}
                filterStatus={filterStatus}
                showAllFilters={showAllFilters}
                onShowAllFilters={() => setShowAllFilters(!showAllFilters)}
                selectedOptions={selectedOptions}
                onSelectedOptionsChange={onSelectedOptionsChange}
                sortOptions={sortOptions}
                selectedSortOption={selectedMobileSortOption}
                onSelectedSortOptionChange={onSelectedSortOptionChange}
                onClearFilter={onClearFilter}
                onClearAllFilters={onClearAllFilters}
                onApplyFilters={onApplyFilters}
                onCloseMobileDialog={onCloseMobileDialog}
              />
            </div>
          </>
        )}
        <SortAndCountWrapper>
          {isLoading ? (
            <MatchingLabelSkeleton />
          ) : (
            <BodyStyled>Showing {totalCount} apps</BodyStyled>
          )}
          <Show above="sm" as="span">
            <SortDropdown
              onSortOptionSelect={handleOnSortOptionSelect}
              selectedSortKey={commitSortKey}
              onDropdownClick={handleOnSortDropdownClick}
            />
          </Show>
        </SortAndCountWrapper>

        {showNoResultView ? (
          <NoResultView
            showClearFilterButton
            title="We couldn't find apps that meet your criteria!"
            description="Try removing or modifying a filter"
            onClearFiltersClick={handleOnClearFiltersClick}
          />
        ) : (
          <PaginatedAppGrid
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            onLoadMoreClick={handleOnLoadMoreClick}
            showLoadMoreButton={showLoadMoreButton}
            renderGrid={() =>
              appList.map((app, index) => (
                <AppTile
                  key={index}
                  tileData={app}
                  environment={environment}
                  source={source}
                  onTileClick={() => handleOnTileClick(app, index)}
                  preferredHosting={
                    collection?.slug === FEDRAMP_PAGE_URL_SLUG
                      ? RegulatedSectorAppTilePreferredHosting.GovernmentCloud
                      : undefined
                  }
                />
              ))
            }
            fetchMoreError={fetchMoreError}
            initialFetchError={initialFetchError}
          />
        )}
      </FluidContainer>
      <BackToTopButton />
    </Container>
  );
};

export default CollectionPage;
